import { Suspense, useEffect, useState } from "react";
import { Routes, Route, Navigate, useNavigate, useParams } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { useApp } from "./context/AppContext";
import { api } from "./services/api";
import { RiskAnalysisProvider, useRiskAnalysisModals } from "./context/RiskAnalysisContext";
import {
  WorkflowExecutionProvider,
  useWorkflowExecutionModals,
} from "./context/WorkflowExecutionContext";
import StageRiskAnalysisModal from "./components/faa/ProcessFlow/StageRiskAnalysisModal";
import WorkflowExecutionStreamModal from "./components/faa/ProcessFlow/WorkflowExecutionStreamModal";

// Components
import Login from "./components/Login";
import Layout from "./components/Layout";
import OfflineNotification from "./components/OfflineNotification";
import { isCertBuddyMember } from "./utils/certbuddyMember";

// FAA Components - Domain-gated for airplanes only

// Automotive Components - Domain-gated for automotive only

// Hooks
import { useFeatures } from "./hooks/useFeatures";
import { lazyPage } from "./utils/lazyPage";

// Pages load when first opened: eager imports put every page's libraries (PDF and Excel
// export, Mermaid, editors, data grid) in one 9 MB main bundle that every visit downloads.
const Compliance = lazyPage(() =>
  import("./components/Compliance").then((m) => ({ default: m.Compliance }))
);
const ProjectDashboard = lazyPage(() => import("./components/ProjectDashboard"));
const CodifierChat = lazyPage(() => import("./components/CodifierChat"));
const AgentList = lazyPage(() => import("./components/agents/AgentList"));
const AgentBuilder = lazyPage(() => import("./components/agents/AgentBuilder"));
const AgentChat = lazyPage(() => import("./components/agents/AgentChat"));
const ArtifactsGallery = lazyPage(() => import("./components/artifacts/gallery/ArtifactsGallery"));
const ArtifactViewPage = lazyPage(() => import("./components/artifacts/gallery/ArtifactViewPage"));
const CorpusAgentChat = lazyPage(() => import("./components/agents/CorpusAgentChat"));
const SkillsManager = lazyPage(() => import("./components/agents/SkillsManager"));
const SourcesPanel = lazyPage(() => import("./components/agents/SourcesPanel"));
const MarketIntelHome = lazyPage(() => import("./components/marketIntel/MarketIntelHome"));
const MarketAnalysisRoute = lazyPage(() => import("./components/marketIntel/MarketAnalysisRoute"));
const ConnectionsPanel = lazyPage(() => import("./components/agents/ConnectionsPanel"));
const RunsInbox = lazyPage(() => import("./components/agents/RunsInbox"));
const SchedulesView = lazyPage(() => import("./components/agents/SchedulesView"));
const LinksPanel = lazyPage(() => import("./components/agents/LinksPanel"));
const Documents = lazyPage(() => import("./components/Documents"));
const GraphExplorer = lazyPage(() => import("./components/GraphExplorer"));
const Tasks = lazyPage(() => import("./components/Tasks"));
const JiraTasks = lazyPage(() => import("./components/JiraTasks"));
const RiskEnhancedView = lazyPage(() => import("./components/RiskEnhancedView"));
const Reports = lazyPage(() => import("./components/Reports"));
const ReportsList = lazyPage(() => import("./components/ReportsList"));
const ReportsPreview = lazyPage(() => import("./components/ReportsPreview"));
const Applications = lazyPage(() => import("./components/Applications"));
const AutofillForm = lazyPage(() => import("./components/AutofillForm"));
const ApplicationAssembly = lazyPage(() => import("./components/ApplicationAssembly"));
const Followups = lazyPage(() => import("./components/Followups"));
const AppTraceability = lazyPage(() => import("./components/AppTraceability"));
const ExecutiveReport = lazyPage(() => import("./components/ExecutiveReport"));
const Approvals = lazyPage(() => import("./components/Approvals"));
const ManageProjects = lazyPage(() => import("./components/ManageProjects"));
const AuthCallback = lazyPage(() => import("./components/AuthCallback"));
const Signup = lazyPage(() => import("./components/Signup"));
const SetPassword = lazyPage(() => import("./components/SetPassword"));
const Suppliers = lazyPage(() => import("./components/suppliers/Suppliers"));
const SupplierPortal = lazyPage(() => import("./components/suppliers/SupplierPortal"));
const SupplierTasks = lazyPage(() => import("./components/suppliers/SupplierTasks"));
const SupplierTaskDetail = lazyPage(() => import("./components/suppliers/SupplierTaskDetail"));
const ParentTaskDetail = lazyPage(() => import("./components/ParentTaskDetail"));
const SupplierDocuments = lazyPage(() => import("./components/suppliers/SupplierDocuments"));
const SupplierCodifier = lazyPage(() => import("./components/suppliers/SupplierCodifier"));
const SupplierWorkflowsPage = lazyPage(() => import("./components/suppliers/SupplierWorkflowsPage"));
const SupplierOnboarding = lazyPage(() => import("./components/suppliers/SupplierOnboarding"));
const SupplierTaskManagement = lazyPage(() => import("./components/suppliers/SupplierTaskManagement"));
const TaskForm = lazyPage(() => import("./components/TaskForm"));
const Profile = lazyPage(() => import("./components/Profile"));
const Unauthorized = lazyPage(() => import("./components/Unauthorized"));
const ViewSupplier = lazyPage(() => import("./components/suppliers/ViewSupplier"));
const GoogleDriveSettings = lazyPage(() => import("./components/GoogleDriveSettings"));
const GoogleDriveCallback = lazyPage(() => import("./components/GoogleDriveCallback"));
const MyRequests = lazyPage(() => import("./components/MyRequests"));
const AdminRequests = lazyPage(() => import("./components/AdminRequests"));
const AgentObservability = lazyPage(() => import("./components/AgentObservability"));
const EvalDashboard = lazyPage(() => import("./components/evals/EvalDashboard"));
const MissingDocuments = lazyPage(() => import("./components/MissingDocuments"));
const RequestDetail = lazyPage(() => import("./components/RequestDetail"));
const ExternalReviewPage = lazyPage(() => import("./components/external/ExternalReviewPage"));
const AuthorityPendingReviews = lazyPage(() => import("./components/external/AuthorityPendingReviews"));
const VehicleClassificationEnhanced = lazyPage(() => import("./components/faa/VehicleClassificationEnhanced"));
const FAAProcessFlow = lazyPage(() => import("./components/faa/FAAProcessFlow"));
const FAAGapAnalysis = lazyPage(() => import("./components/faa/FAAGapAnalysis"));
const DRSQuery = lazyPage(() => import("./components/faa/DRSQuery"));
const AutomotiveClassification = lazyPage(() => import("./components/automotive/AutomotiveClassification"));

const PageLoading = () => (
  <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
    <CircularProgress size={28} />
  </Box>
);

// Old bookmarks used /codifier-chat
const LegacyCertgptRedirect = () => (
  <Navigate to={`/project/${useParams().projectId}/CertGPT`} replace />
);

const ProtectedRoute = ({ children }) => {
  const { user } = useApp();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate("/login");
    }
  }, [user, navigate]);

  if (!user) {
    return <Navigate to="/login" />;
  }

  return <Layout>{children}</Layout>;
};

// Admin-only routes (CertBuddy org o1)
const AdminProtectedRoute = ({ children }) => {
  const { user } = useApp();

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (!isCertBuddyMember(user)) {
    return <Navigate to="/support-requests" replace />;
  }

  return <Layout>{children}</Layout>;
};

const SupplierProtectedRoute = ({ children, requiresOnboarding = true }) => {
  const { user } = useApp();
  const navigate = useNavigate();
  const [supplierProfile, setSupplierProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }

    if (user.role !== "cbSupplier") {
      navigate("/unauthorized");
      return;
    }

    const fetchSupplierProfile = async () => {
      try {
        const profile = await api.getMyProfile();
        setSupplierProfile(profile);

        if (requiresOnboarding && profile.onboarding_status !== "Completed") {
          navigate("/supplier-onboarding");
        }
      } catch (err) {
        console.error("Error fetching supplier profile:", err);
        navigate("/supplier-onboarding");
      } finally {
        setLoading(false);
      }
    };

    fetchSupplierProfile();
  }, [user, navigate, requiresOnboarding]);

  if (!user || user.role !== "cbSupplier") {
    return <Navigate to="/login" />;
  }

  if (loading) {
    return (
      <Layout>
        <div>Loading...</div>
      </Layout>
    );
  }

  if (requiresOnboarding && supplierProfile?.onboarding_status !== "Completed") {
    return <Navigate to="/supplier-onboarding" />;
  }

  return <Layout>{children}</Layout>;
};

// Component that renders all global risk analysis modals
const GlobalRiskAnalysisModals = () => {
  const { modals, closeModal, toggleMinimize } = useRiskAnalysisModals();
  const { modals: execModals } = useWorkflowExecutionModals();

  // Calculate stack index for each minimized modal
  // We need to consider both risk and execution modals for proper stacking
  const minimizedRiskModals = modals.filter((m) => m.isMinimized);
  const minimizedExecModals = execModals.filter((m) => m.isMinimized);
  const _totalMinimizedCount = minimizedRiskModals.length + minimizedExecModals.length;

  return (
    <>
      {modals.map((modal) => {
        // Calculate this modal's stack index (only for minimized modals)
        let stackIndex = 0;
        if (modal.isMinimized) {
          stackIndex = minimizedRiskModals.findIndex((m) => m.key === modal.key);
        }

        return (
          <StageRiskAnalysisModal
            key={`risk-${modal.key}`}
            modalKey={modal.key}
            open={modal.open}
            isMinimized={modal.isMinimized}
            stackIndex={stackIndex}
            onClose={() => closeModal(modal.key)}
            onToggleMinimize={() => toggleMinimize(modal.key)}
            workflowId={modal.workflow.workflow_id}
            stageNumber={modal.workflow.current_stage || 1}
            stageName={`Stage ${modal.workflow.current_stage || 1}`}
            missionName={modal.workflow.mission_name || "Workflow"}
          />
        );
      })}
    </>
  );
};

// Component that renders all global workflow execution modals
const GlobalWorkflowExecutionModals = () => {
  const { modals, closeModal, toggleMinimize } = useWorkflowExecutionModals();
  const { modals: riskModals } = useRiskAnalysisModals();
  const { user, organization } = useApp();

  // Calculate stack index for each minimized modal
  // Execution modals stack AFTER risk modals
  const minimizedRiskModals = riskModals.filter((m) => m.isMinimized);
  const minimizedExecModals = modals.filter((m) => m.isMinimized);

  return (
    <>
      {modals.map((modal) => {
        // Calculate this modal's stack index (only for minimized modals)
        let stackIndex = 0;
        if (modal.isMinimized) {
          // Execution modals stack after risk modals
          stackIndex =
            minimizedRiskModals.length + minimizedExecModals.findIndex((m) => m.key === modal.key);
        }

        return (
          <WorkflowExecutionStreamModal
            key={`exec-${modal.key}`}
            modalKey={modal.key}
            open={modal.open}
            isMinimized={modal.isMinimized}
            stackIndex={stackIndex}
            onClose={() => closeModal(modal.key)}
            onToggleMinimize={() => toggleMinimize(modal.key)}
            workflowId={modal.workflow.workflow_id}
            stageNumber={modal.workflow.current_stage || 1}
            stageName={`Stage ${modal.workflow.current_stage || 1}`}
            missionName={modal.workflow.mission_name || "Workflow"}
            stagePrompt={modal.workflow.stage_prompt || null}
            projectId={modal.workflow.project_id} // Pass project_id for conversation initialization
            user={user}
            organization={organization}
          />
        );
      })}
    </>
  );
};

function AppContent() {
  const { user, featureFlags } = useApp();
  const features = useFeatures();

  return (
    <>
      <OfflineNotification />
      <GlobalRiskAnalysisModals />
      <GlobalWorkflowExecutionModals />
      <Suspense fallback={<PageLoading />}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/set-password" element={<SetPassword />} />
        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route path="/external-review/:token" element={<ExternalReviewPage />} />
        <Route path="/authority-reviews" element={<AuthorityPendingReviews />} />

        {/* Protected routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <ProjectDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/executive-report"
          element={
            <ProtectedRoute>
              <ExecutiveReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="/project/:projectId/dashboard"
          element={
            <ProtectedRoute>
              <ProjectDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/project/:projectId/CertGPT"
          element={
            <ProtectedRoute>
              <CodifierChat />
            </ProtectedRoute>
          }
        />
        <Route path="/project/:projectId/codifier-chat" element={<LegacyCertgptRedirect />} />
        <Route
          path="/agents"
          element={
            <ProtectedRoute>
              <AgentList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/agents/new"
          element={
            <ProtectedRoute>
              <AgentBuilder />
            </ProtectedRoute>
          }
        />
        <Route
          path="/agents/skills"
          element={
            <ProtectedRoute>
              <SkillsManager />
            </ProtectedRoute>
          }
        />
        <Route
          path="/agents/sources"
          element={
            <ProtectedRoute>
              <SourcesPanel />
            </ProtectedRoute>
          }
        />
        <Route
          path="/agents/chains"
          element={
            <ProtectedRoute>
              <LinksPanel />
            </ProtectedRoute>
          }
        />
        <Route
          path="/agents/connections"
          element={
            <ProtectedRoute>
              <ConnectionsPanel />
            </ProtectedRoute>
          }
        />
        <Route
          path="/agents/activity"
          element={
            <ProtectedRoute>
              <RunsInbox />
            </ProtectedRoute>
          }
        />
        <Route
          path="/agents/schedules"
          element={
            <ProtectedRoute>
              <SchedulesView />
            </ProtectedRoute>
          }
        />
        <Route
          path="/agents/:agentId/edit"
          element={
            <ProtectedRoute>
              <AgentBuilder />
            </ProtectedRoute>
          }
        />
        <Route
          path="/artifacts"
          element={
            <ProtectedRoute>
              <ArtifactsGallery />
            </ProtectedRoute>
          }
        />
        <Route
          path="/artifacts/:artifactId"
          element={
            <ProtectedRoute>
              <ArtifactViewPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/agents/:agentId/chat"
          element={
            <ProtectedRoute>
              <AgentChat />
            </ProtectedRoute>
          }
        />
        <Route
          path="/project/:projectId/corpus-agent/:agentId/chat"
          element={
            <ProtectedRoute>
              <CorpusAgentChat />
            </ProtectedRoute>
          }
        />
        <Route
          path="/project/:projectId/tasks"
          element={
            <ProtectedRoute>
              <Tasks />
            </ProtectedRoute>
          }
        />
        <Route
          path="/project/:projectId/tasks/:taskId"
          element={
            <ProtectedRoute>
              <Tasks />
            </ProtectedRoute>
          }
        />
        <Route
          path="/project/:projectId/jira-tasks"
          element={
            <ProtectedRoute>
              <JiraTasks />
            </ProtectedRoute>
          }
        />
        <Route
          path="/project/:projectId/reports"
          element={
            <ProtectedRoute>
              <ReportsList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/project/:projectId/reports/preview"
          element={
            <ProtectedRoute>
              <ReportsPreview />
            </ProtectedRoute>
          }
        />
        <Route
          path="/project/:projectId/reports/:reportId"
          element={
            <ProtectedRoute>
              <Reports />
            </ProtectedRoute>
          }
        />
        <Route
          path="/project/:projectId/applications"
          element={
            <ProtectedRoute>
              <Applications />
            </ProtectedRoute>
          }
        />
        <Route
          path="/project/:projectId/autofill-form"
          element={
            <ProtectedRoute>
              <AutofillForm />
            </ProtectedRoute>
          }
        />
        <Route
          path="/project/:projectId/assembly/:applicationId"
          element={
            <ProtectedRoute>
              <ApplicationAssembly />
            </ProtectedRoute>
          }
        />
        <Route
          path="/project/:projectId/followups"
          element={
            <ProtectedRoute>
              <Followups />
            </ProtectedRoute>
          }
        />
        <Route
          path="/project/:projectId/trace"
          element={
            <ProtectedRoute>
              <AppTraceability />
            </ProtectedRoute>
          }
        />
        <Route
          path="/project/:projectId/approvals"
          element={
            <ProtectedRoute>
              <Approvals />
            </ProtectedRoute>
          }
        />
        <Route
          path="/project/:projectId/settings"
          element={
            <ProtectedRoute>
              <Documents />
            </ProtectedRoute>
          }
        />
        <Route
          path="/knowledge-graph"
          element={
            <ProtectedRoute>
              <GraphExplorer />
            </ProtectedRoute>
          }
        />
        <Route
          path="/manage-projects"
          element={
            <ProtectedRoute>
              <ManageProjects />
            </ProtectedRoute>
          }
        />
        <Route
          path="/project/:projectId/compliance"
          element={
            <ProtectedRoute>
              <Compliance />
            </ProtectedRoute>
          }
        />

        {/* Process Flow - Available for all domains (FAA + compliance workflows) */}
        <Route
          path="/project/:projectId/process-flow/:workflowId?"
          element={
            <ProtectedRoute>
              <FAAProcessFlow />
            </ProtectedRoute>
          }
        />

        {/* Risk and Error Analysis - Available for all domains */}
        <Route
          path="/project/:projectId/risks-and-errors"
          element={
            <ProtectedRoute>
              <RiskEnhancedView />
            </ProtectedRoute>
          }
        />

        <Route
          path="/project/:projectId/risks"
          element={
            <ProtectedRoute>
              <RiskEnhancedView />
            </ProtectedRoute>
          }
        />

        {/* FAA Routes - ONLY for airplanes domain */}
        {features?.faa?.enabled && (
          <>
            <Route
              path="/project/:projectId/classification-analysis"
              element={
                <ProtectedRoute>
                  <VehicleClassificationEnhanced />
                </ProtectedRoute>
              }
            />

            <Route
              path="/project/:projectId/gap-analysis"
              element={
                <ProtectedRoute>
                  <FAAGapAnalysis />
                </ProtectedRoute>
              }
            />

            <Route
              path="/project/:projectId/drs-query"
              element={
                <ProtectedRoute>
                  <DRSQuery />
                </ProtectedRoute>
              }
            />
          </>
        )}

        {/* Automotive Routes - ONLY for automotive domain */}
        {features?.automotive?.enabled && (
          <>
            <Route
              path="/project/:projectId/automotive/classification"
              element={
                <ProtectedRoute>
                  <AutomotiveClassification />
                </ProtectedRoute>
              }
            />
          </>
        )}

        {/* Supplier Routes - Only show if feature flag is enabled */}
        {featureFlags.MARKET_INTEL_ENABLED && (
          <>
            <Route
              path="/market-intel"
              element={
                <ProtectedRoute>
                  <MarketIntelHome />
                </ProtectedRoute>
              }
            />
            <Route
              path="/market-intel/:analysisId"
              element={
                <ProtectedRoute>
                  <MarketAnalysisRoute />
                </ProtectedRoute>
              }
            />
          </>
        )}
        {featureFlags.SUPPLIER_PORTAL_ENABLED && (
          <>
            <Route
              path="/project/:projectId/suppliers"
              element={
                <ProtectedRoute>
                  <Suppliers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/suppliers"
              element={
                <ProtectedRoute>
                  <Suppliers />
                </ProtectedRoute>
              }
            />

            <Route
              path="/supplier-portal"
              element={
                <ProtectedRoute>
                  <SupplierPortal />
                </ProtectedRoute>
              }
            />
            <Route
              path="/supplier-tasks"
              element={
                <SupplierProtectedRoute requiresOnboarding={true}>
                  <SupplierTasks />
                </SupplierProtectedRoute>
              }
            />
            <Route
              path="/supplier-tasks/:taskId"
              element={
                <SupplierProtectedRoute requiresOnboarding={true}>
                  <SupplierTaskDetail />
                </SupplierProtectedRoute>
              }
            />
            <Route
              path="/parent-tasks/:taskId"
              element={
                <ProtectedRoute>
                  <ParentTaskDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/supplier-documents"
              element={
                <SupplierProtectedRoute requiresOnboarding={true}>
                  <SupplierDocuments />
                </SupplierProtectedRoute>
              }
            />
            <Route
              path="/supplier-workflows"
              element={
                <SupplierProtectedRoute requiresOnboarding={true}>
                  <SupplierWorkflowsPage />
                </SupplierProtectedRoute>
              }
            />
            <Route
              path="/supplier-CertGPT"
              element={
                <SupplierProtectedRoute requiresOnboarding={true}>
                  <SupplierCodifier />
                </SupplierProtectedRoute>
              }
            />
            <Route
              path="/supplier-codifier"
              element={<Navigate to="/supplier-CertGPT" replace />}
            />
            <Route
              path="/supplier-onboarding"
              element={
                <SupplierProtectedRoute requiresOnboarding={false}>
                  <SupplierOnboarding />
                </SupplierProtectedRoute>
              }
            />
            <Route
              path="/project/:projectId/suppliers/:supplierId/tasks"
              element={
                <ProtectedRoute>
                  <SupplierTaskManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/project/:projectId/suppliers/:supplierId/tasks/new"
              element={
                <ProtectedRoute>
                  <TaskForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/project/:projectId/suppliers/:supplierId/tasks/:taskId"
              element={
                <ProtectedRoute>
                  <ParentTaskDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/project/:projectId/suppliers/:supplierId/tasks/:taskId/edit"
              element={
                <ProtectedRoute>
                  <TaskForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/project/:projectId/suppliers/:supplierId"
              element={
                <ProtectedRoute>
                  <ViewSupplier />
                </ProtectedRoute>
              }
            />
            <Route
              path="/suppliers/:supplierId/tasks/new"
              element={
                <ProtectedRoute>
                  <TaskForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/suppliers/:supplierId/tasks/:taskId"
              element={
                <ProtectedRoute>
                  <ParentTaskDetail />
                </ProtectedRoute>
              }
            />

            <Route
              path="/suppliers/:supplierId/tasks/:taskId/edit"
              element={
                <ProtectedRoute>
                  <TaskForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/suppliers/:supplierId"
              element={
                <ProtectedRoute>
                  <ViewSupplier />
                </ProtectedRoute>
              }
            />

            <Route
              path="/suppliers/:supplierId/tasks"
              element={
                <ProtectedRoute>
                  <SupplierTaskManagement />
                </ProtectedRoute>
              }
            />
          </>
        )}

        {/* Default route */}
        <Route
          path="/"
          element={
            user ? (
              user.role === "cbSupplier" ? (
                featureFlags.SUPPLIER_PORTAL_ENABLED ? (
                  <Navigate to="/supplier-onboarding" replace />
                ) : (
                  <Navigate to="/unauthorized" replace />
                )
              ) : user.role === "exec" || user.role === "cbExec" ? (
                <Navigate to="/executive-report" replace />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        {/* Google Drive Integration Routes - Settings only */}
        <Route
          path="/settings/google-drive"
          element={
            <ProtectedRoute>
              <GoogleDriveSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/google-drive/callback"
          element={
            <ProtectedRoute>
              <GoogleDriveCallback />
            </ProtectedRoute>
          }
        />

        <Route
          path="/support-requests"
          element={
            <ProtectedRoute>
              <MyRequests />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/support-requests"
          element={
            <AdminProtectedRoute>
              <AdminRequests />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/support-requests/:id"
          element={
            <ProtectedRoute>
              <RequestDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/support-requests/:id"
          element={
            <AdminProtectedRoute>
              <RequestDetail isAdmin />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/observability"
          element={
            <AdminProtectedRoute>
              <AgentObservability />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/evals"
          element={
            <AdminProtectedRoute>
              <EvalDashboard />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/missing-documents"
          element={
            <AdminProtectedRoute>
              <MissingDocuments />
            </AdminProtectedRoute>
          }
        />

        <Route path="/project/:projectId" element={<Navigate to="dashboard" />} />
        <Route path="*" element={<Navigate to="/" replace />} />

        <Route
          path="/project/:projectId/executive-report"
          element={
            <ProtectedRoute>
              <ExecutiveReport />
            </ProtectedRoute>
          }
        />
      </Routes>
      </Suspense>
    </>
  );
}

// Wrap with providers to make modals globally accessible
function App() {
  return (
    <RiskAnalysisProvider>
      <WorkflowExecutionProvider>
        <AppContent />
      </WorkflowExecutionProvider>
    </RiskAnalysisProvider>
  );
}

export default App;
